import { Component, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { Input, Output, EventEmitter, SimpleChanges } from '@angular/core';

import { Events } from '../../helpers/events';

// ionic
import { ModalController, PopoverController } from '@ionic/angular';
// import { PopoverController, Platform } from '@ionic/angular';

// other libraries
// import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";
import { take, takeUntil, debounceTime } from "rxjs/operators";

// components
import { ChatGroupAttendeesActionMenu } from "../chat-group-attendees-action-menu/chat-group-attendees-action-menu.component";

// providers
import { ChatGroupsService } from "../../services/chat-groups.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { PaginatorService } from "../../services/paginator.service";
import { UserService } from "../../services/user.service";

// models
import { ChatGroup } from "../../models/chat-group";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";

@Component({
    selector: 'chat-group-attendees',
    templateUrl: './chat-group-attendees.component.html',
    styleUrls: ['./chat-group-attendees.component.scss']
})
export class ChatGroupAttendeesComponent implements OnInit, OnDestroy, OnChanges {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public data

    /**
     * chat group
     *
     * @type string
     */
    @Input() chatGroup: ChatGroup;

    /**
     * invited filter
     *
     * @type string
     */
    @Input() filterInvited: boolean = false;

    /**
     * fulltext search
     *
     * @type {string}
     */
    public searchTerm: string;

    /**
     * participants
     *
     * @type {Participant[]}
     */
    public items: Participant[] = [];

    itemsTotal: number = 0;
    itemsLimit: number = 50;
    itemsOffset: number = 0;

    pager: any;
    page: number = 1;

    @Output() onDetail: EventEmitter<Participant> = new EventEmitter();

    @Output() onDataLoaded: EventEmitter<any> = new EventEmitter();

    /**
     * check if participant is moderator of chat group
     *
     * @type {boolean}
     */
    public isChatGroupModerator: boolean = false;

    // handlers for events
    chatGroupSelectedHandler;

    /**
     * constructor
     *
     * @param eventService
     * @param appEvents
     */
    constructor(
        public translate: TranslateService,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public paginatorService: PaginatorService,
        public chatGroupsService: ChatGroupsService,
        public modalController: ModalController,
        public popoverController: PopoverController,
        public userService: UserService
    ) {
        this.userLang = this.translate.currentLang;
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        // subscribe to current user
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {

                if (!this.user || this.user.selected_participant_id == user.selected_participant_id) {
                    this.refresh();
                }

                this.user = user;

                // unsubscribe previous handler
                if (this.chatGroupSelectedHandler) {
                    this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
                }

                // attach new one
                this.chatGroupSelectedHandler = (selectedChatGroup?: ChatGroup) => {
                    this.chatGroup = new ChatGroup(selectedChatGroup);
                    this.searchTerm = '';
                    this.refresh();
                }

                this.appEvents.subscribe('messages:chatgroup', this.chatGroupSelectedHandler);

            } else {
                this.user = null;
            }
        });
    }

    /**
     * load more participants in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.refresh(event, false);
    }

    /**
     * Watch changes on Inputs and refresh component if needed
     *
     * @param participant
     */
    ngOnChanges(changes: SimpleChanges) {

        if (
            changes.hasOwnProperty('chatGroup')
        ) {
            this.refresh();
        }
    }

    /**
     * init list
     */
    public refresh($event?, reset = true) {
        if (!this.loading && !this.plt.disableBackgroundRefresh && this.user) {

            // avoid multiple loading of same data
            this.loading = true;

            if (reset) {
                this.itemsOffset = 0;
                this.page = 1;
            }

            if (this.chatGroup) {
                let subscription = this.chatGroupsService.getParticipantList(
                    this.chatGroup.id,
                    this.itemsLimit,
                    this.itemsOffset,
                    {
                        search: {
                            value: this.searchTerm
                        },
                        invited: this.filterInvited
                    }
                ).subscribe((response) => {

                    // empty list when reseting, or if there is not enought items for first page
                    if (reset || (this.itemsOffset == 0 && response['participants'].length < this.itemsLimit)) {

                        this.isChatGroupModerator = false;

                        if (this.chatGroup && this.chatGroup.admins) {
                            // check if current user is moderator
                            this.isChatGroupModerator = this.chatGroup.admins.some((item) => {
                                return item.participant_id == this.user.selected_participant_id && item.role == 'moderator';
                            });
                        }

                        this.items = [];
                    }

                    // move offset only if there are some participants expected in next page...
                    if (response['participants'].length > 0) {
                        this.itemsOffset += this.itemsLimit;
                    }

                    response['participants'].forEach(item => {
                        this.items.push(new Participant(item));
                    });

                    this.itemsTotal = response['total'];
                    this.onDataLoaded.emit({
                        items: this.items,
                        total: this.itemsTotal
                    });

                    this.pager = this.paginatorService.getPager(this.itemsTotal, this.page, this.itemsLimit);

                    this.loading = false;
                },
                    (error) => {
                        this.loading = false;
                        const data = error.error;
                        this.overlayService.showError(data.message);
                        if (data.leave_group) {
                            this.appEvents.publish('messages:refresh:list');
                        }
                    });

            }
        }
    }

    public async openDestroyParticipant(participant: Participant) {
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUP_MEMBER_DELETE_MODAL_TITLE'),
            this.translate.instant('CHAT_GROUP_MEMBER_DELETE_MODAL_HINT', { name: participant.firstname + ' ' + participant.lastname }),
            () => { this.removeParticipant(participant.id) },
            () => { },
            this.translate.instant('CHAT_GROUP_MEMBER_DELETE_MODAL_BUTTON')
        );

        return false;
    }

    public async openAddAdmin(participant: Participant, role = 'owner') {
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUP_ADD_'+role.toUpperCase(), { name: participant.firstname + ' ' + participant.lastname }),
            this.translate.instant('CHAT_GROUP_ADD_'+role.toUpperCase()+'_HINT', { name: participant.firstname + ' ' + participant.lastname }),
            () => { this.addAdmin(participant, role) },
            () => { },
            this.translate.instant('BUTTON_OK')
        );

        return false;
    }

    /**
     * add admin for chat group
     */
    public addAdmin(participant: Participant, role) {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.addAdmin(this.chatGroup.id, participant.id, role).subscribe(
                (response) => {
                    this.loading = false;
                    this.appEvents.publish('chatgroup:admins:changed', response.object);
                    participant.is_moderator = role == 'moderator';
                    participant.is_owner = role == 'owner';
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                    if (data.leave_group) {
                        this.appEvents.publish('messages:refresh:list');
                    }
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    public async openRemoveAdmin(participant: Participant) {
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUP_REMOVE_'+(participant.is_owner ? 'ADMIN' : 'MODERATOR'), { name: participant.firstname + ' ' + participant.lastname }),
            this.translate.instant('CHAT_GROUP_REMOVE_'+(participant.is_owner ? 'ADMIN' : 'MODERATOR')+'_HINT', { name: participant.firstname + ' ' + participant.lastname }),
            () => { this.removeAdmin(participant) },
            () => { },
            this.translate.instant('BUTTON_OK')
        );

        return false;
    }

    /**
     * remove admin from chat group
     */
    public removeAdmin(participant: Participant) {
        if (this.chatGroup) {
            this.loading = true;

            let admin = this.chatGroup.admins.find(item => item.participant_id == participant.id);
            admin.role = null;

            let subscription = this.chatGroupsService.deleteAdmin(admin.id).subscribe(
                (response) => {
                    this.loading = false;
                    this.appEvents.publish('chatgroup:admins:changed', admin);
                    participant.is_moderator = false;
                    participant.is_owner = false;
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                    if (data.leave_group) {
                        this.appEvents.publish('messages:refresh:list');
                    }
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * remove participant from chat group
     */
    public removeParticipant(participantId: number) {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.removeParticipant(this.chatGroup.id, participantId).subscribe(
                (response) => {
                    this.items = this.items.filter((item) => {
                        return item.id != participantId
                    });

                    this.itemsTotal--;
                    this.pager = this.paginatorService.getPager(this.itemsTotal, this.page, this.itemsLimit);

                    this.appEvents.publish('chatGroup:attendees:changed', this.itemsTotal + 1);
                    this.onDataLoaded.emit({
                        items: this.items,
                        total: this.itemsTotal
                    });

                    this.loading = false;
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                    if (data.leave_group) {
                        this.appEvents.publish('messages:refresh:list');
                    }
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    public async openSetParticipantAdmin(participant: Participant) {
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUP_MEMBER_SET_ADMIN_MODAL_TITLE'),
            this.translate.instant('CHAT_GROUP_MEMBER_SET_ADMIN_MODAL_HINT', { name: participant.firstname + ' ' + participant.lastname }),
            () => { this.setParticipantAdmin(participant.id) },
            () => { }
        );

        return false;
    }

    /**
     * set participant as admin
     */
    public setParticipantAdmin(participantId: number) {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.setParticipantAdmin(this.chatGroup.id, participantId).subscribe(
                (response) => {
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;

                    const data = error.error;
                    this.overlayService.showError(data.message);
                    if (data.leave_group) {
                        this.appEvents.publish('messages:refresh:list');
                    }
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * invite participant to chat group
     */
    public invite(participant: Participant) {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.inviteParticipant(this.chatGroup.id, participant.id).subscribe(
                (response) => {
                    this.overlayService.showSuccess(response.message);
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;
                    const data = error.error;
                    this.overlayService.showError(data.message);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }


    /**
     * cancel participant invitation
     */
    public cancelInvitation(participant: Participant) {
        if (this.chatGroup) {
            this.loading = true;

            let subscription = this.chatGroupsService.cancelInvitation(participant.id, this.chatGroup.id).subscribe(
                (response) => {
                    this.items = this.items.filter((item) => {return item.id != participant.id});
                    this.overlayService.showSuccess(response.message);
                    this.loading = false;
                },
                (error) => {
                    this.loading = false;
                    const data = error.error;
                    this.overlayService.showError(data.message);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * function to display attendee detail
     */
    public showAttendeeDetail(participant: Participant) {
        this.onDetail.emit(participant);
    }

    /**
     * show popover with action menu
     * @return void
     */
    async showActionMenu(ev: any, participant: Participant) {
        const popover = await this.popoverController.create({
            component: ChatGroupAttendeesActionMenu,
            event: ev,
            componentProps: {
                participant: participant,
                chatGroup: this.chatGroup
            }
        });

        popover.onWillDismiss().then((data) => {
            if (data && data.data) {
                if (data.data.action == 'remove' && data.data.item) {
                    this.openDestroyParticipant(data.data.item);
                } else if (data.data.action == 'add-admin' && data.data.item && data.data.role) {
                    this.openAddAdmin(data.data.item, data.data.role);
                } else if (data.data.action == 'remove-admin' && data.data.item) {
                    this.openRemoveAdmin(data.data.item);
                }
            }
        });

        await popover.present();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.chatGroupSelectedHandler) {
            this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
